<template>
  <el-card>
    <template #header>API Access</template>
    <el-row :gutter="20">
      <el-col :span="12">
        <template v-if="apiEnabled">
          <el-form label-position="top">
            <el-form-item label="API Key">
              <el-input
                v-model="apiToken"
                :type="showToken ? 'text' : 'password'"
                readonly
                style="font-family: monospace"
              >
                <template #suffix>
                  <el-button
                    link
                    :title="showToken ? 'Hide key' : 'Show key'"
                    @click="showToken = !showToken"
                  >
                    <font-awesome-icon
                      :icon="showToken ? 'eye-slash' : 'eye'"
                    />
                  </el-button>
                </template>
                <template #append>
                  <el-button
                    :title="copied ? 'Copied!' : 'Copy to clipboard'"
                    @click="handleCopy"
                  >
                    <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
                  </el-button>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item>
              <el-space>
                <el-popconfirm
                  title="Generate a new API key? The current key will stop working immediately."
                  confirm-button-text="Rotate"
                  confirm-button-type="warning"
                  @confirm="handleRotate"
                >
                  <template #reference>
                    <el-button type="warning" :loading="loading" plain>
                      Rotate Key
                    </el-button>
                  </template>
                </el-popconfirm>

                <el-popconfirm
                  title="Disable API access? The current key will stop working immediately."
                  confirm-button-text="Disable"
                  confirm-button-type="danger"
                  @confirm="handleDisable"
                >
                  <template #reference>
                    <el-button type="danger" :loading="loading" plain>
                      Disable API
                    </el-button>
                  </template>
                </el-popconfirm>
              </el-space>
            </el-form-item>
          </el-form>
        </template>

        <template v-else>
          <p style="color: #909399; margin-bottom: 16px">
            API access is currently disabled.
          </p>
          <el-button type="primary" :loading="loading" @click="handleEnable">
            Enable API
          </el-button>
        </template>
      </el-col>
    </el-row>
  </el-card>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onActivated } from 'vue'
import { useUserStore } from '@/store/modules/user'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'APIToken',
  setup() {
    const userStore = useUserStore()
    const loading = ref(false)
    const showToken = ref(false)
    const copied = ref(false)

    const apiToken = computed(() => userStore.apiToken)
    const apiEnabled = computed(() => userStore.apiEnabled)

    onActivated(async () => {
      await userStore.GetUserInfo(true)
    })

    function handleCopy() {
      navigator.clipboard.writeText(apiToken.value)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 1500)
    }

    async function handleEnable() {
      loading.value = true
      try {
        await userStore.EnableApiToken()
        showToken.value = true
        ElMessage.success('API access enabled.')
      } catch {
        ElMessage.error('Failed to enable API access.')
      } finally {
        loading.value = false
      }
    }

    async function handleRotate() {
      loading.value = true
      try {
        await userStore.RotateApiToken()
        showToken.value = true
        ElMessage.success('API key rotated successfully.')
      } catch {
        ElMessage.error('Failed to rotate API key.')
      } finally {
        loading.value = false
      }
    }

    async function handleDisable() {
      loading.value = true
      try {
        await userStore.DisableApiToken()
        showToken.value = false
        ElMessage.success('API access disabled.')
      } catch {
        ElMessage.error('Failed to disable API access.')
      } finally {
        loading.value = false
      }
    }

    return {
      apiToken,
      apiEnabled,
      loading,
      showToken,
      copied,
      handleCopy,
      handleEnable,
      handleRotate,
      handleDisable
    }
  }
})
</script>
